import React, { useState, useEffect } from 'react';
import {
  Typography, Box, Paper, Grid, Chip, Divider, Button,
  CircularProgress, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Card, CardContent,
  Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, FormControl, InputLabel, Select, MenuItem,
  TextField
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusRemarks, setStatusRemarks] = useState('');
  const [submittingStatus, setSubmittingStatus] = useState(false);

  // Check if user is an association admin
  const isAssociationAdmin = user?.is_association_admin && user?.role === 'subadmin';
  const isSubadmin = user?.role === 'subadmin';

  // Fetch customer data
  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/customers/${id}`);
        setCustomer(response.data);
      } catch (error) {
        console.error('Error fetching customer details:', error);
        setError('Failed to load customer details');
        toast.error('Failed to load customer details');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomer();
  }, [id]);

  const handleBack = () => {
    navigate('/customers');
  };

  const handleStatusDialogOpen = () => {
    setNewStatus(customer?.status || '');
    setStatusRemarks('');
    setStatusDialogOpen(true);
  };

  const handleStatusDialogClose = () => {
    setStatusDialogOpen(false);
  };

  const handleSubmitStatusChange = async () => {
    if (!newStatus) {
      toast.error('Please select a status');
      return;
    }

    try {
      setSubmittingStatus(true);
      
      const data = {
        customer_id: parseInt(id),
        requested_status: newStatus,
        remarks: statusRemarks,
      };
      
      // For association admins, allow direct update
      if (isAssociationAdmin) {
        data.direct_update = true;
      }
      
      await api.post('/api/customers/status-change', data);
      
      toast.success(isAssociationAdmin 
        ? 'Customer status updated successfully' 
        : 'Status change request submitted successfully');
      
      // Refresh customer data
      const response = await api.get(`/api/customers/${id}`);
      setCustomer(response.data);
      
      setStatusDialogOpen(false);
    } catch (error) {
      console.error('Error changing status:', error);
      toast.error(error.response?.data?.error || 'Failed to change status');
    } finally {
      setSubmittingStatus(false);
    }
  };

  const formatStatus = (status) => {
    switch (status.toLowerCase()) {
      case 'normal':
        return 'Normal';
      case 'over_exposure':
      case 'overexposure':
      case 'over exposure':
        return 'Over Exposure';
      case 'defaulter':
        return 'Defaulter';
      case 'blacklisted':
        return 'Blacklisted';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 3 }}>
          Back to Customers
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={4}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mr: 2 }}>
          Back
        </Button>
        <Typography variant="h4">Customer Details</Typography>
      </Box>

      {customer && (
        <>
          {/* Customer Summary */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="h5" gutterBottom>
                  {customer.name}
                </Typography>
                <Chip
                  label={formatStatus(customer.status)}
                  variant="filled"
                  sx={{ 
                    mb: 2,
                    ...(customer.status.toLowerCase() === 'normal' && {
                      bgcolor: 'success.main',
                      color: 'success.contrastText',
                      '& .MuiChip-label': {
                        color: 'success.contrastText',
                      }
                    }),
                    ...(['over exposure', 'over_exposure', 'overexposure'].includes(customer.status.toLowerCase()) && {
                      bgcolor: 'info.main',
                      color: 'info.contrastText',
                      '& .MuiChip-label': {
                        color: 'info.contrastText',
                      }
                    }),
                    ...(customer.status.toLowerCase() === 'defaulter' && {
                      bgcolor: 'warning.main',
                      color: 'warning.contrastText',
                      '& .MuiChip-label': {
                        color: 'warning.contrastText',
                      }
                    }),
                    ...(customer.status.toLowerCase() === 'blacklisted' && {
                      bgcolor: 'error.main',
                      color: 'error.contrastText',
                      '& .MuiChip-label': {
                        color: 'error.contrastText',
                      }
                    })
                  }}
                />
                {customer.gstin && (
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    GSTIN: {customer.gstin}
                  </Typography>
                )}
                {customer.mobile_no && (
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    Mobile: {customer.mobile_no}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Association: {customer.association.name}
                </Typography>
              </Box>
              {isSubadmin && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleStatusDialogOpen}
                >
                  Change Status
                </Button>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Total Outstanding
                    </Typography>
                    <Typography variant="h5" component="div">
                      ₹{customer.total_outstanding.toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      {user?.role === 'superadmin' ? 'Highest Distributor Outstanding' : 'Your Outstanding'}
                    </Typography>
                    <Typography variant="h5" component="div">
                      ₹{customer.your_outstanding.toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Created On
                    </Typography>
                    <Typography variant="h5" component="div">
                      {new Date(customer.created_at).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>

          {/* Distributor Breakdown - only for association admins */}
          {(user?.role === 'superadmin' || isAssociationAdmin) && customer.distributor_breakdown && customer.distributor_breakdown.length > 0 && (
            <Paper sx={{ p: 3, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Distributor Name Breakdown
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Distributor Name</TableCell>
                      <TableCell align="right">Outstanding Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customer.distributor_breakdown.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.distributor_name}</TableCell>
                        <TableCell align="right">₹{item.amount.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Status Change History */}
          {customer.status_changes && customer.status_changes.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <HistoryIcon sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Status Change History
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Previous Status</TableCell>
                      <TableCell>Requested Status</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Requested By</TableCell>
                      <TableCell>Approved By</TableCell>
                      <TableCell>Remarks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customer.status_changes.map((change, index) => (
                      <TableRow key={index}>
                        <TableCell>{new Date(change.requested_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Chip
                            label={formatStatus(change.previous_status)}
                            size="small"
                            variant="filled"
                            sx={{
                              ...(change.previous_status.toLowerCase() === 'normal' && {
                                bgcolor: 'success.main',
                                color: 'success.contrastText',
                                '& .MuiChip-label': {
                                  color: 'success.contrastText',
                                }
                              }),
                              ...(['over exposure', 'over_exposure', 'overexposure'].includes(change.previous_status.toLowerCase()) && {
                                bgcolor: 'info.main',
                                color: 'info.contrastText',
                                '& .MuiChip-label': {
                                  color: 'info.contrastText',
                                }
                              }),
                              ...(change.previous_status.toLowerCase() === 'defaulter' && {
                                bgcolor: 'warning.main',
                                color: 'warning.contrastText',
                                '& .MuiChip-label': {
                                  color: 'warning.contrastText',
                                }
                              }),
                              ...(change.previous_status.toLowerCase() === 'blacklisted' && {
                                bgcolor: 'error.main',
                                color: 'error.contrastText',
                                '& .MuiChip-label': {
                                  color: 'error.contrastText',
                                }
                              })
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={formatStatus(change.requested_status)}
                            size="small"
                            variant="filled"
                            sx={{
                              ...(change.requested_status.toLowerCase() === 'normal' && {
                                bgcolor: 'success.main',
                                color: 'success.contrastText',
                                '& .MuiChip-label': {
                                  color: 'success.contrastText',
                                }
                              }),
                              ...(['over exposure', 'over_exposure', 'overexposure'].includes(change.requested_status.toLowerCase()) && {
                                bgcolor: 'info.main',
                                color: 'info.contrastText',
                                '& .MuiChip-label': {
                                  color: 'info.contrastText',
                                }
                              }),
                              ...(change.requested_status.toLowerCase() === 'defaulter' && {
                                bgcolor: 'warning.main',
                                color: 'warning.contrastText',
                                '& .MuiChip-label': {
                                  color: 'warning.contrastText',
                                }
                              }),
                              ...(change.requested_status.toLowerCase() === 'blacklisted' && {
                                bgcolor: 'error.main',
                                color: 'error.contrastText',
                                '& .MuiChip-label': {
                                  color: 'error.contrastText',
                                }
                              })
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={change.status.charAt(0).toUpperCase() + change.status.slice(1)}
                            size="small"
                            variant="filled"
                            sx={{
                              ...(change.status === 'approved' && {
                                bgcolor: 'success.main',
                                color: 'success.contrastText',
                                '& .MuiChip-label': {
                                  color: 'success.contrastText',
                                }
                              }),
                              ...(change.status === 'pending' && {
                                bgcolor: 'warning.main',
                                color: 'warning.contrastText',
                                '& .MuiChip-label': {
                                  color: 'warning.contrastText',
                                }
                              }),
                              ...(change.status === 'rejected' && {
                                bgcolor: 'error.main',
                                color: 'error.contrastText',
                                '& .MuiChip-label': {
                                  color: 'error.contrastText',
                                }
                              })
                            }}
                          />
                        </TableCell>
                        <TableCell>{change.requested_by}</TableCell>
                        <TableCell>{change.approved_by || '-'}</TableCell>
                        <TableCell>{change.remarks || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Status Change Dialog */}
          <Dialog
            open={statusDialogOpen}
            onClose={handleStatusDialogClose}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Change Customer Status</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                {isAssociationAdmin
                  ? "As an Association Admin, you can directly change this customer's status."
                  : "Request a status change for this customer. This will require approval from an Association Admin."}
              </DialogContentText>
              <FormControl fullWidth sx={{ mb: 2 }} disabled={submittingStatus}>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  value={newStatus}
                  label="Status"
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  {/* Only show status options that are allowed for this association */}
                  {(customer.association?.allowed_status || ['Normal', 'Defaulter']).map((status) => (
                    <MenuItem key={status} value={status.toLowerCase().replace(' ', '_')}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Remarks"
                multiline
                rows={4}
                fullWidth
                value={statusRemarks}
                onChange={(e) => setStatusRemarks(e.target.value)}
                placeholder="Provide the reason for this status change"
                disabled={submittingStatus}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleStatusDialogClose} disabled={submittingStatus}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitStatusChange} 
                variant="contained" 
                color="primary" 
                disabled={submittingStatus || customer?.status === newStatus}
              >
                {submittingStatus ? <CircularProgress size={24} /> : 'Submit'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default CustomerDetail;